/// <amd-module name="Build/PipelineInsights/RunsInsights/Sources/RunsInsightsSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { AnalyticsODataClientBase } from "Analytics/Shared/Common/Sources/AnalyticsODataClientBase";
import { IReportContext, ITrendReportConfiguration, TrendBy } from "Analytics/Shared/Common/Types";
import { getDateFilter } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { expr } from "Analytics/Shared/FilterExpression";
import { ODataQuery } from "Analytics/Shared/QueryBuilder";
import { getPercentage } from "Analytics/Shared/Utils/Number";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";

interface IODataInsights {
    PartiallySucceededCount?: number;
    SucceededCount?: number;
    TotalCount?: number;
    PassPercentage: number;
}

export interface IPipelineInsightsData {
    passPercentage: number;
    initialValue: number;
    finalValue: number;
}

export class RunsInsightsSource extends AnalyticsODataClientBase {
    private _buildEntityName: string = `${PipelineEntities.PipelineRuns}`;

    constructor(pageContext: IVssPageContext) {
        super("Pipelines.RunInsights", pageContext);
    }

    public async queryPipelineInsightsData(reportContext: IReportContext, confValues: ITrendReportConfiguration): Promise<IPipelineInsightsData> {
        const projectId = this._getProjectId();
        const queryOptions = {
            entityType: this._buildEntityName,
            project: projectId
        } as ODataQueryOptions;

        const query = new ODataQuery()
            .filter(
                expr(this._getPipelineContextFilter(reportContext))
                    .and.expr(getDateFilter(TrendBy.Days, confValues.period))
                    .and.expr("CanceledCount ne 1")
            )
            .groupBy("CompletedDateSK")
            .aggregate(
                "$count as TotalCount",
                "PartiallySucceededCount with sum as PartiallySucceededCount",
                "SucceededCount with sum as SucceededCount"
            )
            .orderBy("CompletedDateSK", "asc");

        queryOptions.$apply = query.apply;
        queryOptions.$orderby = query.orderby;

        const pipelineTrendData = await this._queryPassRateTrendData(queryOptions);
        return pipelineTrendData;
    }

    private _getPipelineContextFilter(reportContext: IReportContext) {
        const queryField = `${PipelineEntities.PipelineId}`;
        const queryValue = reportContext.definitionId;

        return `${queryField} eq ${queryValue}`;
    }

    private async _queryPassRateTrendData(queryOptions: ODataQueryOptions): Promise<IPipelineInsightsData> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("Data is empty");
        }

        let pipelineInsightsData: IPipelineInsightsData;
        let totalSucceededCount = 0;
        let totalPartiallySucceededCount = 0;
        let totalCount = 0;
        const dataArray: IODataInsights[] = [];
        let insights: IODataInsights;

        const trendData = data.value as IODataInsights[];
        if (trendData.length === 0) {
            return { passPercentage: 0, initialValue: 0, finalValue: 0 };
        }

        // Iterate over all data
        for (insights of trendData) {
            const totalCountForDay = (insights.TotalCount as number) || 0;
            const succeededCount = (insights.SucceededCount as number) || 0;
            const partiallySucceededCount = (insights.PartiallySucceededCount as number) || 0;

            const passPercentage = getPercentage(totalCountForDay, succeededCount + partiallySucceededCount);
            totalCount += totalCountForDay;
            totalSucceededCount += succeededCount;
            totalPartiallySucceededCount += partiallySucceededCount;

            dataArray.push({
                PassPercentage: passPercentage
            });
        }

        pipelineInsightsData = {
            passPercentage: getPercentage(totalCount, totalSucceededCount + totalPartiallySucceededCount),
            initialValue: dataArray[0].PassPercentage || 0,
            finalValue: dataArray[dataArray.length - 1].PassPercentage || 0
        };
        return pipelineInsightsData;
    }
}
